/**
 * Онбординг O-01 — единственный экран первого запуска.
 *
 * Изменение v1.11: раньше онбординг занимал семь шагов (возраст и согласия → подбор →
 * обязательные поля → фото → интересы → приватность → правила) и на телефоне это были
 * минуты до первого экрана приложения. Теперь спрашиваем ровно то, без чего аккаунта не
 * существует — имя, дату рождения и согласие, — а фото предлагаем здесь же и необязательно,
 * как в Telegram на шаге «Your Info». Город, интересы и параметры подбора спрашиваются
 * по месту: при включении «Движа» (P-07 → U-06) и в профиле; приватность работает на
 * безопасных значениях по умолчанию и меняется в настройках.
 */
import { useNavigate } from '@tanstack/react-router'
import { useRef, useState } from 'react'

import { api, idempotency, ifMatch, unwrap, uuid } from '@shared/api/client'
import { ApiError } from '@shared/api/errors'
import type { OnboardingMissing } from '@shared/api/types'
import { acceptAttribute } from '@shared/media/limits'
import { useHasPointerFine } from '@shared/lib/use-media-query'
import { Button, Field, Icon, Input, Spinner, cx, useToast } from '@shared/ui'

import { useMediaUpload } from '@features/media/use-media-upload'

import { LegalConsentNote } from '../legal/LegalLinks'

import { refreshMe } from '../../boot/bootstrap'
import { useAppStore } from '../../model/app-store'
import styles from './onboarding.module.css'

/** Что именно осталось заполнить — человеку, а не счётчиком (O-07). */
const MISSING_LABEL: Record<OnboardingMissing, string> = {
  consents: 'согласие с правилами',
  birth_date: 'дата рождения',
  display_name: 'имя',
}

export function OnboardingScreen(): React.ReactElement {
  const navigate = useNavigate()
  const toast = useToast()
  const me = useAppStore((s) => s.me)
  const config = useAppStore((s) => s.config)
  // На телефоне автофокус сразу поднимает клавиатуру и прячет аватар — фокусируем только с мышью.
  const autoFocusName = useHasPointerFine()

  const [name, setName] = useState(me?.profile.display_name ?? '')
  const [birthDate, setBirthDate] = useState('')
  const [nameError, setNameError] = useState<string | null>(null)
  const [birthError, setBirthError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const birthDateSet = me?.profile.birth_date_set ?? false
  const ready = name.trim().length >= 2 && (birthDateSet || birthDate !== '')

  /* --- Фото: необязательное, грузится сразу после выбора ---------------- */

  const fileInputRef = useRef<HTMLInputElement>(null)
  const upload = useMediaUpload({ config, purpose: 'profile_photo', accountStatus: me?.user.status })
  /*
   * Загрузка идёт, пока человек печатает имя, поэтому к «Продолжить» она обычно уже готова.
   * Если нет — ждём именно этот промис, а не опрашиваем состояние: иначе выбранное фото
   * молча потерялось бы при быстром нажатии.
   */
  const uploadRef = useRef<Promise<string | null> | null>(null)

  const pickPhoto = (file: File | undefined): void => {
    // §10.1 п. 2: отмена выбора файла — не ошибка, молчим.
    if (!file) return
    uploadRef.current = upload.uploadFile(file)
  }

  const photoPreview = upload.state.previewUrl
  const photoBusy = upload.state.stage !== 'idle' && upload.state.stage !== 'ready'

  /* --- Завершение ------------------------------------------------------- */

  const submit = async (): Promise<void> => {
    setSaving(true)
    setNameError(null)
    setBirthError(null)

    try {
      /*
       * Согласие даётся нажатием «Продолжить» — текст сноски стоит прямо над кнопкой.
       * Три документа всё так же фиксируются версиями (A-05), просто без отдельного тумблера.
       */
      if (config) {
        await api.POST('/me/consents', {
          body: {
            documents: [
              { document: 'terms', version: config.legal.terms.version },
              { document: 'privacy', version: config.legal.privacy.version },
              { document: 'community_rules', version: config.legal.community_rules.version },
            ],
          } as never,
        })
      }

      unwrap(
        await api.PATCH('/me/profile', {
          body: {
            display_name: name.trim(),
            ...(birthDateSet ? {} : { birth_date: birthDate }),
          } as never,
          ...(me ? ifMatch(me.profile.version) : {}),
        }),
      )

      const mediaId = await uploadRef.current
      if (mediaId) {
        unwrap(
          await api.POST('/me/profile/photos', {
            body: { media_id: mediaId } as never,
            ...idempotency(uuid()),
          }),
        )
        uploadRef.current = null
      }

      unwrap(await api.POST('/me/onboarding/complete'))
      await refreshMe()
      useAppStore.getState().setPhase('ready')
      void navigate({ to: '/dvizh' })
    } catch (error) {
      // A-06 «Регистрация невозможна по возрасту»: нейтральное объяснение на поле даты.
      if (error instanceof ApiError && error.code === 'age_not_allowed') {
        setBirthError('Сервис доступен только совершеннолетним пользователям.')
      } else if (error instanceof ApiError && error.code === 'validation_failed') {
        const fields = error.detailFields()
        if (fields.includes('display_name')) setNameError('Проверьте имя.')
        if (fields.includes('birth_date') || !fields.length) setBirthError('Проверьте дату рождения.')
      } else if (error instanceof ApiError && error.code === 'conflict') {
        // 409 на complete: `details[].field` — значение OnboardingMissing (BS §5.7).
        const missing = [...error.detailFields(), ...error.detailCodes()].find(
          (value): value is OnboardingMissing => value in MISSING_LABEL,
        )
        await refreshMe()
        toast.show(missing ? `Не хватает: ${MISSING_LABEL[missing]}` : 'Заполните имя и дату рождения')
      } else {
        toast.show('Не удалось сохранить', { tone: 'danger' })
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={styles['page']}>
      <main className={styles['main']}>
        <form
          className={styles['card']}
          onSubmit={(event) => {
            event.preventDefault()
            if (ready && !saving) void submit()
          }}
        >
          <div className={styles['avatarBlock']}>
            <button
              type="button"
              className={styles['avatar']}
              onClick={() => fileInputRef.current?.click()}
              aria-label={photoPreview ? 'Изменить фото' : 'Добавить фото'}
            >
              {photoPreview ? (
                <img className={styles['avatarPhoto']} src={photoPreview} alt="" />
              ) : (
                <Icon name="camera" size={28} />
              )}

              {photoBusy ? (
                <span className={styles['avatarBusy']}>
                  <Spinner size={20} />
                </span>
              ) : null}

              {photoPreview && !photoBusy ? (
                <span className={styles['avatarEdit']}>
                  <Icon name="edit" size={14} />
                </span>
              ) : null}
            </button>

            <button
              type="button"
              className={styles['avatarAction']}
              onClick={() => fileInputRef.current?.click()}
            >
              {photoPreview ? 'Изменить фото' : 'Добавить фото'}
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept={acceptAttribute(upload.limit)}
            // §11.3 п. 4: скрытое поле остаётся в дереве доступности.
            aria-label="Выбрать фотографию"
            className={styles['hiddenInput']}
            onChange={(event) => {
              pickPhoto(event.target.files?.[0])
              event.target.value = ''
            }}
          />

          <h1 className={styles['title']}>Как вас зовут?</h1>
          <p className={styles['lead']}>Имя и фото увидят другие. Изменить можно в любой момент.</p>

          {/* U-14: причина, по которой фото не взялось, — рядом с полями, а не баннером сверху */}
          {upload.state.error ? <p className={styles['photoError']}>{upload.state.error}</p> : null}

          <Field label="Имя" error={nameError}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={40}
                placeholder="Как к вам обращаться"
                autoFocus={autoFocusName}
              />
            )}
          </Field>

          {/* Дата рождения — единственная формальность: сервис с 18 лет (A-05) */}
          {!birthDateSet ? (
            <Field
              label="Дата рождения"
              error={birthError}
              hint="Сервис с 18 лет. Дату потом не изменить, другим её не показываем — только возраст."
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  invalid={invalid}
                  type="date"
                  value={birthDate}
                  onChange={(event) => setBirthDate(event.target.value)}
                  max={new Date().toISOString().slice(0, 10)}
                />
              )}
            </Field>
          ) : null}

          <div className={cx(styles['submit'], !birthDateSet && styles['submitTight'])}>
            <LegalConsentNote />

            <Button type="submit" variant="primary" size="lg" fullWidth loading={saving} disabled={!ready}>
              Продолжить
            </Button>
          </div>
        </form>
      </main>
    </div>
  )
}
